import { useEffect, useMemo } from 'react'

import type { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'

import { QueryLoadingManager } from '@b-stock/fe-core/bstock-next/components'
import {
  gsspMiddleware,
  getGsspAccessToken,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  AnalyticsSource,
  AnalyticsEntityType,
  NestableAnalyticsProvider,
} from '@b-stock/fe-core/bstock-react'

import { StorefrontIdProvider } from '@/app/buy/seller/[sellerSlug]/StorefrontContext'
import { PforExclusionProvider } from '@/buyer-portal/components/features/listing/hooks/PforExclusionContext'
import { ListingPageErrorHandler } from '@/buyer-portal/components/features/listing/ListingPageErrorHandler'
import { CardLayout } from '@/buyer-portal/components/layouts/CardLayout'
import StandardLayout from '@/buyer-portal/components/layouts/StandardLayout'
import demandParam from '@/buyer-portal/helpers/demandParam'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { ListingDetailsPageContents } from '@/buyer-portal/pages/listings/details/ListingDetailsPageContents'
import { FeatureFlags } from '@/helpers/featureFlags'
import { shouldShowManifestSection } from '@/helpers/listing/shouldShowManifestSection'
import { getExcludedPforStorefrontIds } from '@/helpers/pforExclusion'
import LiveChatWidgetGate from '@/helpers/telemetry/LiveChatWidgetGate'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useDealBadgeDataLDP } from '@/hooks/useDealBadgeData'
import listingDetailsQuery from '@/queries/listingDetailsQuery'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'
import { watchedStatusQuery } from '@/queries/watchlistQuery'

type ListingDetailsGsspProps = {
  referral_url: string
  excludedPforStorefrontIds: string[]
}

const ListingDetailsPage = ({
  referral_url,
  excludedPforStorefrontIds,
}: ListingDetailsGsspProps) => {
  const { query } = useRouter()
  const accountId = useAccountId()
  const listingId = demandParam(query, 'id')
  const initialBidAmount = query.bidAmount as string | undefined
  const listingQueryResult = listingDetailsQuery.useQuery(
    listingId,
    accountId,
    true
  )

  const listing = listingQueryResult.data
  // Get deal badge data for both display and analytics
  const dealBadgeData = useDealBadgeDataLDP(listing)

  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })

  const analyticsData = useMemo(
    () => ({
      screenName: 'listing_detail_page',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listingId,
      seller_display_name: listing?.seller.account.displayName,
      storefront_name: listing?.seller.storefront?.name,
      storefront_id: listing?.storefrontId,
      deal_badge_value: dealBadgeData.deal_badge_value,
      is_deal_badge_shown: dealBadgeData.is_deal_badge_shown,
      shouldShowManifest: listing ? shouldShowManifestSection(listing) : true,
      is_new_seller:
        isNewSellerTreatment &&
        (newSellerIds?.has(listing?.storefrontId ?? '') ?? false),
    }),
    [listing, listingId, dealBadgeData, isNewSellerTreatment, newSellerIds]
  )

  const { errorViewed } = useAnalytics()

  // Side-effect for errors
  useEffect(() => {
    if (listingQueryResult.status === 'error') {
      errorViewed({
        screenName: 'listing_page_error',
        source: AnalyticsSource.HOME_PORTAL,
        intention: 'buyer',
        entityType: AnalyticsEntityType.BUYER,
        errorType: 'technical',
        errorMessage: 'listing detail error',
        listingId,
        message: listingQueryResult.error.message,
      })
    }
  }, [listingQueryResult, errorViewed, listingId])

  // Get storefrontId from listing query result for Header context
  const storefrontId = listingQueryResult.data?.storefrontId

  const enableLdpRedesign = useFlag(FeatureFlags.ENABLE_LDP_REDESIGN)
  const LayoutComponent = enableLdpRedesign ? CardLayout : StandardLayout

  return (
    <StorefrontIdProvider storefrontId={storefrontId}>
      <LayoutComponent>
        <LiveChatWidgetGate />
        <NestableAnalyticsProvider value={analyticsData}>
          <QueryLoadingManager
            {...listingQueryResult}
            ErrorComponent={ListingPageErrorHandler}
          >
            {({ data: listing }) => (
              <PforExclusionProvider
                excludedPforStorefrontIds={excludedPforStorefrontIds}
              >
                <ListingDetailsPageContents
                  referral_url={referral_url}
                  listing={listing}
                  initialBidAmount={initialBidAmount}
                />
              </PforExclusionProvider>
            )}
          </QueryLoadingManager>
        </NestableAnalyticsProvider>
      </LayoutComponent>
    </StorefrontIdProvider>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const listingId = demandParam(ctx.params, 'id')
  const tokens = await getGsspAccessToken(ctx)
  const { accountId, personId } = tokens?.decoded ?? {}

  // For analytics
  const referral_url = ctx.req.headers.referer ?? ''

  const excludedPforStorefrontIds = await getExcludedPforStorefrontIds()

  const prefetchQueries = [
    listingDetailsQuery.prefetch(listingId, accountId, true),
    ...(personId // if user is logged out we don't need to fetch
      ? [watchedStatusQuery.prefetch({ personId, listingId })]
      : []),
  ]

  return gsspMiddleware({
    ctx,
    prefetchQueries,
    props: {
      referral_url,
      excludedPforStorefrontIds,
    } satisfies ListingDetailsGsspProps,
  })
}

export default ListingDetailsPage
